/** @format */

import React, { useState, useMemo } from 'react';
import { Table, Input, Button, Space, Spin, Empty, Select, Checkbox, InputNumber } from 'antd';
import { SearchOutlined, ReloadOutlined, EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { CallLog, CallLogsFilterInput, CallLogUpdateInput } from '../types/callLogs.types';
import { useCallLogs } from '../hooks/useCallLogs';
import formatCurrency from 'utils/formatCurrency';

interface CallLogsTableProps {
  filter?: CallLogsFilterInput;
  refreshTrigger?: number;
  onRefresh?: () => void;
}

const CallLogsTable: React.FC<CallLogsTableProps> = ({
  filter,
  refreshTrigger = 0,
  onRefresh,
}) => {
  const { data, loading, error, updateCallLog } = useCallLogs(filter, refreshTrigger);
  const [searchText, setSearchText] = useState('');
  const [pageSize, setPageSize] = useState(50);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<CallLogUpdateInput>({});

  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!searchText) return data;

    return data.filter((log) => {
      const searchLower = searchText.toLowerCase();
      const billableText = log.billable ? 'billable' : 'not billable';
      return (
        (log.agentName && log.agentName.toLowerCase().includes(searchLower)) ||
        (log.vendorName && log.vendorName.toLowerCase().includes(searchLower)) ||
        (log.callFrom && log.callFrom.toLowerCase().includes(searchLower)) ||
        (log.statusName && log.statusName.toLowerCase().includes(searchLower)) ||
        billableText.includes(searchLower)
      );
    });
  }, [data, searchText]);

  const handleEdit = (record: CallLog) => {
    setEditingId(record._id);
    setEditingData({
      statusName: record.statusName,
      billable: record.billable,
      cost: record.cost,
    });
  };

  const handleSave = async (id: string) => {
    await updateCallLog(id, editingData);
    setEditingId(null);
    setEditingData({});
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditingData({});
  };

  const columns: ColumnsType<CallLog> = [
    {
      title: 'Call Date',
      dataIndex: 'callDate',
      key: 'callDate',
      sorter: (a, b) =>
        new Date(a.callDate).getTime() - new Date(b.callDate).getTime(),
      render: (date: string | Date) => {
        if (!date) return 'N/A';
        try {
          const utcDate = new Date(date);
          if (isNaN(utcDate.getTime())) {
            return 'Invalid Date';
          }
          const correctedTime = new Date(
            utcDate.getTime() + 8 * 60 * 60 * 1000
          );
          return correctedTime.toLocaleString(undefined, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          });
        } catch (error) {
          return 'Error';
        }
      },
      width: 160,
    },
    {
      title: 'Call Length (s)',
      dataIndex: 'callLength',
      key: 'callLength',
      sorter: (a, b) => a.callLength - b.callLength,
      width: 120,
      render: (length: number) => `${length}s`,
    },
    {
      title: 'Call From',
      dataIndex: 'callFrom',
      key: 'callFrom',
      width: 120,
    },
    {
      title: 'Call To',
      dataIndex: 'callTo',
      key: 'callTo',
      width: 120,
    },
    {
      title: 'Queue ID',
      dataIndex: 'queueId',
      key: 'queueId',
      sorter: (a, b) => a.queueId - b.queueId,
      width: 90,
    },
    {
      title: 'Agent Name',
      dataIndex: 'agentName',
      key: 'agentName',
      sorter: (a, b) => (a.agentName || '').localeCompare(b.agentName || ''),
      width: 130,
    },
    {
      title: 'Vendor Name',
      dataIndex: 'vendorName',
      key: 'vendorName',
      sorter: (a, b) => (a.vendorName || '').localeCompare(b.vendorName || ''),
      width: 130,
    },
    {
      title: 'Billable',
      dataIndex: 'billable',
      key: 'billable',
      width: 80,
      filters: [
        { text: 'Billable', value: true },
        { text: 'Not Billable', value: false },
      ],
      onFilter: (value, record) => record.billable === value,
      render: (billable: boolean, record: CallLog) =>
        editingId === record._id ? (
          <Checkbox
            checked={editingData.billable !== undefined ? editingData.billable : billable}
            onChange={(e) =>
              setEditingData({ ...editingData, billable: e.target.checked })
            }
          />
        ) : (
          <Checkbox checked={billable} disabled />
        ),
    },
    {
      title: 'Cost',
      dataIndex: 'cost',
      key: 'cost',
      sorter: (a, b) => a.cost - b.cost,
      width: 100,
      render: (cost: number, record: CallLog) =>
        editingId === record._id ? (
          <InputNumber
            value={editingData.cost}
            onChange={(value) =>
              setEditingData({ ...editingData, cost: value || 0 })
            }
            precision={2}
            style={{ width: '100%' }}
            size='small'
          />
        ) : (
          formatCurrency(cost)
        ),
    },
    {
      title: 'Status',
      dataIndex: 'statusName',
      key: 'statusName',
      width: 110,
      render: (status: string, record: CallLog) =>
        editingId === record._id ? (
          <Select
            value={editingData.statusName || status}
            onChange={(value) =>
              setEditingData({ ...editingData, statusName: value })
            }
            style={{ width: '100%' }}
            size='small'
          >
            <Select.Option value='Completed'>Completed</Select.Option>
            <Select.Option value='Cancelled'>Cancelled</Select.Option>
            <Select.Option value='Reviewed'>Reviewed</Select.Option>
            <Select.Option value='Pending'>Pending</Select.Option>
          </Select>
        ) : (
          status
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record: CallLog) =>
        editingId === record._id ? (
          <Space size='small'>
            <Button
              type='primary'
              size='small'
              icon={<SaveOutlined />}
              onClick={() => handleSave(record._id)}
            />
            <Button size='small' icon={<CloseOutlined />} onClick={handleCancel} />
          </Space>
        ) : (
          <Button
            type='link'
            size='small'
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
        ),
    },
  ];

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p style={{ color: 'red' }}>Error: {error}</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
        <Input
          placeholder='Search by Agent, Vendor, Phone, or Status'
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ flex: 1, maxWidth: '400px' }}
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={onRefresh}
          loading={loading}
        >
          Refresh
        </Button>
      </div>

      <Spin spinning={loading}>
        {filteredData.length === 0 ? (
          <Empty description='No call logs found' />
        ) : (
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey={(record) => record._id}
            pagination={{
              pageSize,
              pageSizeOptions: ['25', '50', '100', '200'],
              onShowSizeChange: (_, size) => setPageSize(size),
              showSizeChanger: true,
              showTotal: (total) => `Total: ${total} calls`,
            }}
            scroll={{ x: 1200 }}
          />
        )}
      </Spin>
    </div>
  );
};

export default CallLogsTable;
